import { useEffect, useRef, useState } from "react";
import {
    Button,
    Card,
    Image,
    Input,
    Modal,
    Popconfirm,
    Spin,
    Tag,
    message,
} from "antd";

import {
    Upload,
    Trash2,
} from "lucide-react";
import dayjs from "dayjs";
import {
    getAppointmentPhotos,
    uploadAppointmentPhotos,
    deleteAppointmentPhoto,
    AppointmentPhoto,
} from "../../api/AppointmentCare";
import { usePermission } from "../../hooks/usePermission";

const TYPE_LABELS: Record<string, string> = {
    treatment: "Điều trị",
    before: "Trước điều trị",
    after: "Sau điều trị",
    invoice: "Hóa đơn",
};

interface Props {
    appointmentId?: number;
}

export default function GalleryTab({ appointmentId }: Props) {
    const { can } = usePermission("care");
    const [photos, setPhotos] = useState<AppointmentPhoto[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const fetchPhotos = () => {
        if (!appointmentId) { setPhotos([]); return; }
        setLoading(true);
        getAppointmentPhotos(appointmentId, { limit: 100 }).then((res) => {
            setPhotos(res.status ? res.items : []);
            setLoading(false);
        });
    };

    useEffect(() => {
        fetchPhotos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    // Chọn file xong sẽ hiện danh sách ảnh để nhập tiêu đề riêng từng ảnh trước khi tải lên
    // (API chỉ nhận title lúc upload nên mỗi ảnh được upload bằng một request riêng kèm title của nó)
    const [pendingItems, setPendingItems] = useState<{ file: File; title: string; preview: string }[]>([]);

    const clearPending = () => {
        pendingItems.forEach((it) => URL.revokeObjectURL(it.preview));
        setPendingItems([]);
    };

    const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!appointmentId || !files.length) return;
        const valid: File[] = [];
        for (const f of files) {
            if (!f.type.startsWith("image/")) {
                message.warning(`"${f.name}" không phải file ảnh`);
                continue;
            }
            if (f.size > 10 * 1024 * 1024) {
                message.warning(`"${f.name}" vượt quá 10MB`);
                continue;
            }
            valid.push(f);
        }
        if (!valid.length) return;
        setPendingItems(valid.map((file) => ({ file, title: "", preview: URL.createObjectURL(file) })));
    };

    const handleUpload = async () => {
        if (!appointmentId || !pendingItems.length) return;
        setUploading(true);
        const results = await Promise.all(
            pendingItems.map((it) =>
                uploadAppointmentPhotos(appointmentId, [it.file], { title: it.title.trim() || undefined })
            )
        );
        setUploading(false);
        const okCount = results.filter((r) => r.status).length;
        clearPending();
        if (okCount > 0) {
            message.success(`Đã tải lên ${okCount} ảnh`);
            fetchPhotos();
        }
        if (okCount < results.length) {
            const firstErr = results.find((r) => !r.status);
            message.error(firstErr?.msg || "Một số ảnh tải lên thất bại");
        }
    };

    const handleDelete = async (photoId: number) => {
        const res = await deleteAppointmentPhoto(photoId);
        if (res.status) {
            message.success(res.msg || "Đã xóa ảnh");
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
        } else {
            message.error(res.msg || "Xóa ảnh thất bại");
        }
    };

    return (
        <div className="p-5 bg-slate-50 pb-[60px] dark:bg-[#1f1f1f] text-000000e0">

            <div className="flex justify-between items-center mb-5">

                <h2 className="text-lg font-semibold">
                    Hình ảnh điều trị {photos.length > 0 ? `(${photos.length})` : ""}
                </h2>

                {can("create") && (
                    <>
                        <Button
                            icon={<Upload size={16} />}
                            loading={uploading}
                            onClick={() => inputRef.current?.click()}
                        >
                            Tải ảnh lên
                        </Button>

                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={handleFiles}
                        />
                    </>
                )}

            </div>

            <Spin spinning={loading}>

                {photos.length === 0 && !loading ? (
                    <div className="py-16 text-center text-slate-400 font-medium">
                        Chưa có hình ảnh nào
                    </div>
                ) : (
                    <Image.PreviewGroup>

                        <div className="grid grid-cols-2 gap-4">

                            {photos.map((item) => (

                                <Card
                                    key={item.id}
                                    bodyStyle={{ padding: 10 }}
                                    className="rounded-xl"
                                >

                                    <div className="relative">

                                        {/* Ẩn thẻ loại ảnh theo yêu cầu
                                        {item.type && (
                                            <Tag
                                                color="processing"
                                                className="absolute top-3 left-3 z-10"
                                            >
                                                {TYPE_LABELS[item.type] || item.type}
                                            </Tag>
                                        )} */}

                                        {can("delete") && (
                                            <Popconfirm
                                                title="Xóa hình ảnh này?"
                                                okText="Đồng ý"
                                                cancelText="Hủy"
                                                okButtonProps={{ danger: true }}
                                                onConfirm={() => handleDelete(item.id)}
                                            >
                                                <Button
                                                    danger
                                                    size="small"
                                                    shape="circle"
                                                    icon={<Trash2 size={13} />}
                                                    className="!absolute top-3 right-3 z-10"
                                                />
                                            </Popconfirm>
                                        )}

                                        <Image
                                            src={item.image}
                                            className="rounded-lg object-cover"
                                            height={230}
                                            width="100%"
                                        />

                                    </div>

                                    <div className="text-center mt-3">

                                        <h4 className="font-semibold text-[15px] text-000000e0">
                                            {item.title || "—"}
                                        </h4>

                                        <p className="text-slate-400 text-sm">
                                            {item.created_at ? dayjs(item.created_at).format("DD/MM/YYYY") : ""}
                                        </p>

                                    </div>

                                </Card>

                            ))}

                        </div>

                    </Image.PreviewGroup>
                )}

            </Spin>

            <Modal
                open={pendingItems.length > 0}
                title={`Tải lên ${pendingItems.length} ảnh`}
                okText="Tải lên"
                cancelText="Hủy"
                confirmLoading={uploading}
                onOk={handleUpload}
                onCancel={clearPending}
            >
                <div className="mb-3 text-slate-600 text-sm">
                    Nhập tiêu đề riêng cho từng ảnh (bỏ trống sẽ tự sinh):
                </div>
                <div className="max-h-[360px] overflow-y-auto space-y-3 pr-1">
                    {pendingItems.map((it, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <img
                                src={it.preview}
                                alt={it.file.name}
                                className="w-14 h-14 rounded-lg object-cover border flex-shrink-0"
                            />
                            <Input
                                placeholder={`Tiêu đề ảnh ${i + 1} (VD: Ảnh sau điều trị 3 ngày)`}
                                value={it.title}
                                onChange={(e) =>
                                    setPendingItems((prev) =>
                                        prev.map((p, idx) => (idx === i ? { ...p, title: e.target.value } : p))
                                    )
                                }
                                maxLength={255}
                            />
                        </div>
                    ))}
                </div>
            </Modal>

        </div>
    );
}
